// packages/0-foundation/core.module/src/v1/_universal/lvl_1/utils/debug-flag.fn.ts
//
// Persisted debug toggle for browser bundles: `?<param>=1` in the URL saves
// the flag to localStorage — from that point the flag is on and survives
// reloads; `?<param>=0` turns it off and removes the flag. All browser globals
// are behind typeof-guards — safe to call from non-browser runtimes (always
// false there). Fail-soft: storage access can throw (private mode, disabled
// cookies) — treated as "flag off".

export interface IDebugFlag {
  /** URL query param that toggles the flag (`?<param>=1` on, `?<param>=0` off). */
  param: string;
  /** localStorage key that persists the flag across reloads. */
  storageKey: string;
}

/** Log level the flag enables (consola level 4 — debug visible). */
export const DEBUG_FLAG_LOG_LEVEL = 4;

export function isDebugFlagEnabled(flag: IDebugFlag): boolean {
  try {
    if (typeof window === "undefined") return false;
    const param = new URLSearchParams(window.location.search).get(flag.param);
    if (param === "1") {
      window.localStorage.setItem(flag.storageKey, "1");
    } else if (param === "0") {
      window.localStorage.removeItem(flag.storageKey);
    }
    return window.localStorage.getItem(flag.storageKey) === "1";
  } catch {
    return false;
  }
}

/**
 * Raises `baseLevel` to DEBUG_FLAG_LOG_LEVEL when the flag is enabled —
 * the flag can only add verbosity, never reduce it.
 */
export function resolveDebugFlagLevel(
  baseLevel: number,
  flag: IDebugFlag | undefined,
): number {
  if (flag !== undefined && isDebugFlagEnabled(flag)) {
    return Math.max(baseLevel, DEBUG_FLAG_LOG_LEVEL);
  }
  return baseLevel;
}
